import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames/bind';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import * as styles from './BrightEdgeAutopilot.css';
import { getPageType, constructSEODefaultCanonicalUrl } from '../../helpers/Utils/seoHelper';
import { getBrightEdgeAutopilotData } from '../../actions/BrightEdgeAction';
import { selectRequestUrl, selectSeoConfig } from '../../selectors/ContextSelector';
import {
    selectBrightEdgeData,
    selectKey,
    selectBodyNode,
    selectDefaultPageGroups,
    selectDefaultPageGroupNodes,
} from '../../selectors/BrightEdgeSelector';

const cx = classNames.bind(styles);

const BrightEdgeAutopilot = () => {
    const dispatch = useDispatch();
    const [mounted, setMounted] = useState(false);

    const requestUrl = useSelector((state) => selectRequestUrl(state));
    const { search = null } = useLocation();
    const pageType = getPageType(requestUrl);
    const hostName = 'www.jcpenney.com';

    const { excludeQueryParams = [], includeQueryParams = [] } = useSelector((state) =>
        selectSeoConfig(state)
    );
    const brightEdgeData = useSelector((state) => selectBrightEdgeData(state));
    const key = useSelector((state) => selectKey(state));
    const content = useSelector((state) => selectBodyNode(state));
    const defaultGroups = useSelector((state) => selectDefaultPageGroups(state));
    const defaultNodes = useSelector((state) => selectDefaultPageGroupNodes(state));

    const currentUrl = () => {
        const validParams = () => {
            if (pageType === 'g' || pageType === 'p') {
                return constructSEODefaultCanonicalUrl(
                    search,
                    excludeQueryParams,
                    includeQueryParams,
                    false
                );
            }
            return null;
        };

        const params = validParams();

        return `https://${hostName}${requestUrl}${params || ''}`;
    };

    if (!mounted) {
        // Code for componentWillMount here. This code is called only one time before intial render

        if (isEmpty(brightEdgeData)) {
            const url = currentUrl();
            dispatch(getBrightEdgeAutopilotData({ url }));
        }
    }

    useEffect(() => {
        setMounted(true);
    }, []);

    const renderDefaultCapsuleLinks = () => {
        const url = currentUrl();
        let groupNodeName = 'excluded_config';

        const checkRules = (rules) => {
            return rules?.some((regexRule) => url?.match(regexRule));
        };

        // eslint-disable-next-line no-restricted-syntax
        for (const group of defaultGroups) {
            // eslint-disable-next-line camelcase
            const { exclude_rules, include_rules } = group;

            const excludeRulesValidation = exclude_rules?.length && checkRules(exclude_rules);

            if (excludeRulesValidation) {
                break;
            } else {
                const includeRulesValidation = include_rules?.length && checkRules(include_rules);

                if (includeRulesValidation) {
                    groupNodeName = group?.name;
                    break;
                }
            }
        }

        return defaultNodes[groupNodeName]?.[0]?.content ?? null;
    };

    const renderLinks = useMemo(() => {
        let displayContent = content;

        if (key === '-0') {
            displayContent = renderDefaultCapsuleLinks();
        }

        return (
            <section
                className={`${dt(['bg-white', 'clear-both', 'text-xsmall', 'text-gray-60'])} ${cx(
                    'container'
                )}`}
                data-automation-id="brightEdge-links"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: displayContent }}
            />
        );
    }, [key]);

    return isEmpty(brightEdgeData) ? null : renderLinks;
};

export default BrightEdgeAutopilot;
